/* eslint-disable react-hooks/exhaustive-deps */

import { Paywall } from '@monorepo-hol/design-system';
import { UserInfo } from '@monorepo-hol/providers';

import { DetailPage, DocumentViewer, LoadingState } from '../../../components';
import { checkIfAuthorized } from '../../../helper/utils';
import { isPrecedentCatalog, mappingAcl } from '../../../helper/catalog';
import NotFoundPage from '../../../pages/404';
import type { DataDocumentDetail } from '../services/document-detail/types';
import type { SubmenuType } from '../../../components/types';
import { type GetDocumentDetailData } from '../services/document-detail';
import { getDocumentAccess, getMetaData } from '../utils';
import { AppendicesType } from '../../../components/DocumentViewer/DocumentViewer';
import { useAppendicesData } from '../hooks/useAppendicesData';
import type { AppendiceType } from '../types';
import UnexpectedError from '../../../components/ErrorMessage/UnexpectedError/UnexpectedError';
import { MainCatalog } from '../../../types/catalog';
import { useStartTrace } from '../hooks/useStartTrace';
import { PdfProvider } from '../../../provider/PdfProvider';
import type { MetaDataProps } from '../../../types/metadata';
import { type ToolbarProps } from '../../../components/HeaderDetail/Toolbar';
import { FooterNavPdf } from '../../../components/FooterNavPdf';
import { useDownloadTrigger } from '@pd/hooks/useDownloadTrigger';
import { useDownloadPdf } from '@pd/hooks/useDownloadPdf';

export interface AppendicesLayoutProps {
  type: AppendiceType;
  guid: string;
  user: UserInfo;
  docId?: string;
  dataDocument: GetDocumentDetailData;
  data: DataDocumentDetail;
  subMenu: SubmenuType;
  catalogData: MainCatalog;
  documentList: any[];
  is404: boolean;
}

export default function AppendicesLayout(props: AppendicesLayoutProps) {
  const {
    type,
    guid,
    user,
    docId,
    dataDocument,
    data,
    subMenu,
    catalogData,
    documentList,
    is404,
  } = props;
  const catalogAcl = mappingAcl(data);

  useStartTrace({ guid });

  const { handleDownloadClick, isDownloadAuthorized, isUserLoggedIn } = useDownloadPdf();

  useDownloadTrigger({
    onTriggerDownload: handleDownloadClick,
    enabled: isDownloadAuthorized && isUserLoggedIn,
  });

  const {
    LangState,
    isDocumentReady,
    pdfDoc,
    pdfDocList,
    isAppendiceDataLoading,
    isDocumentError,
  } = useAppendicesData({
    guid,
    data,
    user,
    docId,
    catalogAcl,
    type,
    catalogData,
  });

  const checkPaywallConditions = () => {
    const isPeraturanUnauthorized =
      data?.profile_guid !== 'kutu_putusan' &&
      !checkIfAuthorized(data?.catalog_access_right);
    const isPutusanUnauthorized =
      data?.profile_guid === 'kutu_putusan' &&
      !checkIfAuthorized(data?.access_right?.kaidah_hukum);
    const isPrecedentUnauthorized =
      data?.profile_guid === 'kutu_putusan' &&
      isPrecedentCatalog(data) &&
      checkIfAuthorized(data?.access_right?.kaidah_hukum);

    const conditions: boolean =
      {
        main:
          !isAppendiceDataLoading &&
          (isPeraturanUnauthorized ||
            isPutusanUnauthorized ||
            isPrecedentUnauthorized) &&
          isNonLoginOrUserFree,
        consolidations: isNonLoginOrUserFree,
        translations: isNonLoginOrUserFree,
      }[type] || false;

    return conditions;
  };

  const isNonLoginOrUserFree: boolean =
    (user && user?.package === 'member') || !user;

  const isToolbarShown = !checkPaywallConditions();

  const renderPaywall = () => {
    const conditions = checkPaywallConditions();
    if (conditions) {
      return (
        <Paywall
          isUserLoggedIn={user !== undefined}
          additionalQuery={{
            utm_source: 'pusat_data',
            utm_medium: 'pusatdata',
            utm_campaign: 'subscription_paywall',
          }}
          withUtmTerm
          locale={LangState.language}
        />
      );
    }
    return null;
  };

  const isNotfound =
    (data !== undefined && Object.keys(data).length === 0) ||
    dataDocument?.status === 404 ||
    !data ||
    !catalogData?.mainDocument;

  const appendicesType: AppendicesType = {
    main: 'appendices' as AppendicesType,
    consolidations: 'appendices_consolidation' as AppendicesType,
    translations: 'appendices_translation' as AppendicesType,
  }[type];

  const toolbarProps: ToolbarProps = {
    slug: data?.slug,
    lampiranList: catalogData?.documentList,
    isBackShown: true,
    appendicesType,
    documentList,
    mainDocumentData: catalogData.mainDocument,
    catalogData,
    smartDocData: undefined,
    pdfData: pdfDoc,
    fileType: pdfDoc?.type,
    subMenu: 'lampiran',
  };

  const activeMenu = {
    main: 'document',
    consolidations: 'consolidations',
    translations: 'translations',
  }[type];

  const renderDocumentViewer = () => {
    const { isPeraturan, isPrecedent, isPutusan } = getDocumentAccess(data);
    const isTranslation =
      data?.profile_guid !== 'kutu_putusan' &&
      checkIfAuthorized(catalogAcl?.translations);
    const isConsolidation =
      data?.profile_guid !== 'kutu_putusan' &&
      checkIfAuthorized(catalogAcl?.consolidations);
    const conditions: boolean =
      {
        'main': isPeraturan || isPrecedent || isPutusan,
        'consolidations': isConsolidation,
        'translations': isTranslation,
      }[type] || false;

    if (conditions) {
      if (isDocumentError) {
        return (
          <div className="py-28">
            <UnexpectedError message="Terjadi Kesalahan saat memuat dokumen" />
          </div>
        );
      } else {
        return (
          <DocumentViewer
            pdfData={pdfDoc}
            smartDocData={undefined}
            lampiranList={catalogData?.documentList}
            documentList={documentList}
            appendicesType={appendicesType}
            isBackShown
            mainDocumentData={catalogData?.mainDocument}
            isDocumentShowed={isDocumentReady}
            catalogData={catalogData}
            slug={data?.slug}
            headerDetailProps={{
              activeMenu,
              catalogList: pdfDocList,
              subMenu,
              metadata: getMetaData(data),
              toolbarProps,
            }}
            is404={is404}
            // activeMenu={activeMenu}
          />
        );
      }
    }

    return null;
  };

  if (isNotfound) {
    return (
      <>
        <NotFoundPage />
        <FooterNavPdf />
      </>
    );
  } else {
    return (
      <PdfProvider catalogData={catalogData}>
        <DetailPage
          data={data}
          subMenu={subMenu}
          catalogList={pdfDocList}
          activeMenu={activeMenu}
          toolbarProps={toolbarProps}
          isToolbarShown={isToolbarShown && !is404}
        >
          {renderDocumentViewer()}
          {renderPaywall()}
        </DetailPage>
        <FooterNavPdf />
      </PdfProvider>
    );
  }
}
